import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import './App.scss';
import Header from './components/header/Header';
import { Toast } from 'primereact/toast';
import Sidebar from './components/sidebar/Sidebar';
import type { RootState } from './app/store';
import { addLocale } from 'primereact/api';

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { store } from './app/store';
import Profile from './features/profile/Profile';
import ProtectedRoute from './components/ProtectedRoute';
import Session from './auth/Session';
import { useAuth } from './auth/AuthContext';
import ResetPassword from './features/login/ResetPassword';
import ResetPasswordConfirm from './features/login/ResetPasswordConfirm';
import ResetPasswordExpired from './features/login/ResetPasswordExpired';
import ActivateUserExpired from './features/login/ActivateUserExpired';
import ResetActivation from './features/login/ResetActivation';
import ActivationConfirm from './features/login/ActivationConfirm';
import AddDriver from './features/fleet/driver/AddDriver';
import UsetifulLoader from './components/UsetifulLoader';
import activateReducer from './app/activateSlice';
import SentryError from './features/sentry/SentryError';
import Configuration from './features/fleet/configuration/Configuration';

interface DeviceState {
  isMobile: boolean;
}

const initialDeviceState: DeviceState = {
  isMobile: window.innerWidth < 768,
};

const deviceSlice = createSlice({
  name: 'device',
  initialState: initialDeviceState,
  reducers: {
    setIsMobile(state, action: PayloadAction<boolean>) {
      state.isMobile = action.payload;
    },
  },
});

export const { setIsMobile } = deviceSlice.actions;

if (!(store as any).asyncReducers) {
  (store as any).asyncReducers = {};
}
if (!(store as any).asyncReducers.device) {
  (store as any).asyncReducers.device = deviceSlice.reducer;
  store.replaceReducer(
    (state: any, action: any) => ({
      ...state,
      device: deviceSlice.reducer(state?.device, action),
      activeIdent: activateReducer(state?.activeIdent, action),
      userIdent: (store as any).asyncReducers.userIdent
        ? (store as any).asyncReducers.userIdent(state?.userIdent, action)
        : state?.userIdent,
    })
  );
}

const Login = React.lazy(() => import('./features/login/Login'));
const Dashboard = React.lazy(() => import('./features/dashboard/Dashboard'));
const Fleet = React.lazy(() => import('./features/fleet/Fleet'));
const Offer = React.lazy(() => import('./features/offer/Offer'));
const Orders = React.lazy(() => import('./features/orders/Orders'));
const OrderDetails = React.lazy(() => import('./features/orders/OrderDetails'));
const Transactions = React.lazy(() => import('./features/transactions/Transactions'));
const Documents = React.lazy(() => import('./features/documents/Documents'));
const Contact = React.lazy(() => import('./features/contact/Contact'));
const CarDetails = React.lazy(() => import('./features/fleet/car/CarDetails'));
const DriverDetails = React.lazy(() => import('./features/fleet/driver/DriverDetails'));
const OneOffSuccess = React.lazy(() => import('./features/payments/OneOffSuccess'));
const OneOffError = React.lazy(() => import('./features/payments/OneOffError'));

const AppShell: React.FC<{ isMobile: boolean; sidebarVisible: boolean; setSidebarVisible: (v: boolean) => void; }> = ({ isMobile, sidebarVisible, setSidebarVisible }) => {
  const location = useLocation();
  const isLoginRoute = location.pathname === '/login/';
  const isPaymentSuccessRoute = location.pathname.startsWith('/payment-success/');
  const isPaymentErrorRoute = location.pathname.startsWith('/payment-error/');
  const isActivationConfirmRoute = location.pathname.startsWith('/activate-confirm/');
  const isActivationExpiredRoute = location.pathname.startsWith('/activation-expired/');
  const isActivationResetRoute = location.pathname.startsWith('/reset-activate/');
  const { isAuthenticated, isInitializing } = useAuth();
  const showChrome =
    !isLoginRoute
    && !isInitializing
    && isAuthenticated
    && !isPaymentSuccessRoute
    && !isPaymentErrorRoute;

  const toast = React.useRef<Toast>(null);

  React.useEffect(() => {
    try {
      const raw = sessionStorage.getItem('flashToast');
      if (raw) {
        const cfg = JSON.parse(raw);
        toast.current?.show(cfg);
        sessionStorage.removeItem('flashToast');
      }
    } catch {}
  }, [location.pathname]);

  addLocale('pl', {
    firstDayOfWeek: 1,
    dayNames: ['niedziela', 'poniedziałek', 'wtorek', 'środa', 'czwartek', 'piątek', 'sobota'],
    dayNamesShort: ['ndz', 'pon', 'wt', 'śr', 'czw', 'pt', 'sob'],
    dayNamesMin: ['Nd', 'Pn', 'Wt', 'Śr', 'Cz', 'Pt', 'So'],
    monthNames: [
      'styczeń', 'luty', 'marzec', 'kwiecień', 'maj', 'czerwiec',
      'lipiec', 'sierpień', 'wrzesień', 'październik', 'listopad', 'grudzień'
    ],
    monthNamesShort: [
      'sty', 'lut', 'mar', 'kwi', 'maj', 'cze',
      'lip', 'sie', 'wrz', 'paź', 'lis', 'gru'
    ],
    today: 'Dziś',
    clear: 'Wyczyść'
  });

  return (
    <>
      {
        !isActivationConfirmRoute
        && !isActivationExpiredRoute
        && !isActivationResetRoute
        && <Session />}
      {showChrome && <UsetifulLoader />}
      {showChrome && (
        <Sidebar
          visible={!isMobile || sidebarVisible}
          onHide={() => setSidebarVisible(false)}
        />
      )}
      <div style={{ marginLeft: showChrome && !isMobile ? 220 : 0, transition: 'margin-left 0.3s' }}>
        <Toast ref={toast} />
        {showChrome && <Header onMenuClick={() => setSidebarVisible(true)} />}
          <Routes>
            <Route path="/sentry-debug/" element={<SentryError />} />
            <Route path="/login/" element={<Login />} />
            <Route path="/reset-password/" element={<ResetPassword />} />
            <Route path="/reset-password-confirm/*" element={<ResetPasswordConfirm />} />
            <Route path="/reset-password-expired/" element={<ResetPasswordExpired />} />
            <Route path="/reset-activate/" element={<ResetActivation />} />
            <Route path="/activate-confirm/*" element={<ActivationConfirm />} />
            <Route path="/activation-expired/" element={<ActivateUserExpired />} />
            <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/fleet/" element={<ProtectedRoute><Fleet /></ProtectedRoute>} />
            <Route path="/fleet/cars/:carId/" element={<ProtectedRoute><CarDetails /></ProtectedRoute>} />
            <Route path="/fleet/drivers/:driverId/" element={<ProtectedRoute><DriverDetails /></ProtectedRoute>} />
            <Route path="/fleet/add-driver/" element={<ProtectedRoute><AddDriver /></ProtectedRoute>} />
            <Route path="/fleet/configuration/" element={<ProtectedRoute><Configuration /></ProtectedRoute>} />
            <Route path="/offer/" element={<ProtectedRoute><Offer /></ProtectedRoute>} />
            <Route path="/orders/" element={<ProtectedRoute><Orders /></ProtectedRoute>} />
            <Route path="/orders/:orderId/" element={<ProtectedRoute><OrderDetails /></ProtectedRoute>} />
            <Route path="/transactions/" element={<ProtectedRoute><Transactions /></ProtectedRoute>} />
            <Route path="/documents/" element={<ProtectedRoute><Documents /></ProtectedRoute>} />
            <Route path="/contact/" element={<ProtectedRoute><Contact /></ProtectedRoute>} />
            <Route path="/profile/" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
            <Route path="/payment-success/:uuid" element={<ProtectedRoute><OneOffSuccess /></ProtectedRoute>} />
            <Route path="/payment-error/:uuid" element={<ProtectedRoute><OneOffError /></ProtectedRoute>} />
          </Routes>
      </div>
    </>
  );
};

const App: React.FC = () => {
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const dispatch = useDispatch();
  const isMobile = useSelector((state: RootState & { device: DeviceState }) => state.device?.isMobile ?? false);

  React.useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      dispatch(setIsMobile(mobile));
      if (window.innerWidth >= 768) setSidebarVisible(false);
    };
    window.addEventListener('resize', handleResize);
    dispatch(setIsMobile(window.innerWidth < 768));
    return () => window.removeEventListener('resize', handleResize);
  }, [dispatch]);

  return (
    <Router>
      <AppShell isMobile={isMobile} sidebarVisible={sidebarVisible} setSidebarVisible={setSidebarVisible} />
    </Router>
  );
};

export default App;